import { getConfigEntitySchemaWithDomainEntityAttributesAndRelationships } from '@web-config-app/core';
import type {
  EntityDetailSchema,
  ConfigEntityDetail,
  EntityDependencyRef,
} from '@web-config-app/core';
import type { LinkedEntityReference } from '../get-entity-reference-ids/get-entity-reference-ids';
import {
  getEntityReferenceIds,
  isLinkedEntityRef,
} from '../get-entity-reference-ids/get-entity-reference-ids';
import type {
  LoadEntityRelationshipsSchema,
  FindConfigEntityByDomainEntityId,
} from '../../types/operation.types';
import { extractRelationshipConfigEntityRefs } from './extract-relationship-config-entity-ids';
import { resolveSchema, type ResolveSchemaContext } from './resolve-schema';
import { isRetryableError } from '../batch-async-operations/batch-async-operations';

/**
 * Represents an entity along with its direct dependencies.
 * Used for building a dependency graph for topological sorting.
 */
export interface EntityWithDependencies {
  /** The entity data */
  entity: ConfigEntityDetail;
  /** Direct dependencies of this entity (entities it references) */
  dependencies: EntityDependencyRef[];
}

/**
 * Represents a failure to discover or fetch a referenced entity.
 * These are tracked separately so callers can surface warnings to users.
 */
export interface DiscoveryFailure {
  /** Type of failure that occurred */
  type: 'domain_resolution_failed' | 'fetch_failed' | 'no_data_returned';
  /** The domain entity ID that was being resolved (if applicable) */
  domainEntityId?: string;
  /** The domain entity type that was being resolved (if applicable) */
  domainEntityType?: string;
  /** The config entity ID that failed to fetch (if applicable) */
  configEntityId?: string;
  /** Human-readable error message */
  message: string;
  /** The parent entity that contained this reference (for context) */
  referencedFrom?: string;
}

/**
 * Result of getAllReferencedEntities including both successes and failures.
 */
export interface GetAllReferencedEntitiesResult {
  /** Successfully discovered entities with their dependencies */
  entities: Map<string, EntityWithDependencies>;
  /** Failures that occurred during discovery (entities that could not be fetched) */
  failures: DiscoveryFailure[];
  /** Schemas loaded during discovery, keyed by domain entity type */
  schemas: Map<string, EntityDetailSchema>;
}

/** Number of retry attempts for transient failures */
const MAX_RETRY_ATTEMPTS = 2;

/** Base delay in milliseconds for exponential backoff */
const RETRY_BASE_DELAY_MS = 500;

interface WithRetryConfig<T> {
  /** The async operation to retry */
  operation: () => Promise<T>;
  /**
   * Optional error handler called when all attempts fail (or a non-retryable error occurs).
   * When provided, `withRetry` returns `undefined` instead of throwing.
   */
  onError?: (error: Error) => void;
}

/**
 * Retries an async operation with exponential backoff for retryable errors.
 * Uses isRetryableError to determine if an error should be retried (429, 5xx, network errors).
 * Non-retryable errors (4xx client errors) fail immediately without retry.
 *
 * @returns The result of the operation. When `onError` is provided, returns `undefined` on failure
 *   instead of throwing.
 */
async function withRetry<T>({
  operation,
  onError,
}: WithRetryConfig<T>): Promise<T | undefined> {
  let lastError: Error = new Error('Operation failed');

  for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop -- Intentional: retry logic requires sequential attempts
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Only retry retryable errors (429, 5xx, network errors)
      // Non-retryable errors (4xx client errors) should fail immediately
      if (!isRetryableError(error)) {
        break;
      }

      // Only wait and retry if we have attempts remaining
      if (attempt < MAX_RETRY_ATTEMPTS - 1) {
        // Exponential backoff: 500ms, 1000ms, 2000ms, etc.
        const delay = RETRY_BASE_DELAY_MS * 2 ** attempt;
        // eslint-disable-next-line no-await-in-loop -- Intentional: need to wait before retry
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });
      }
    }
  }

  if (onError) {
    onError(lastError);
    return undefined;
  }

  throw lastError;
}

interface GetAllReferencedEntitiesParams {
  entityData?: Partial<ConfigEntityDetail>;
  entitySchema: EntityDetailSchema;
  loadEntityRelationshipsSchemas: LoadEntityRelationshipsSchema;
  findEntityOnSourceByDomainEntityId: FindConfigEntityByDomainEntityId;
  fetchEntityOnSourceById: (
    configEntityId: string,
    domainEntityType?: string,
  ) => Promise<{
    data?: any;
    error?: Error;
  }>;
}

/**
 * Recursively finds every entity referenced by the provided entity data.
 *
 * This function performs a depth-first traversal through entity references,
 * following two types of links:
 * - Schema-declared domain entity references (x-entity-reference annotations)
 * - JSON:API `relationships` entries containing config-entity IDs
 *
 * Each discovered entity is fetched with all its relationships included,
 * and the process continues until the full transitive closure is collected.
 *
 * This function also tracks and returns discovery failures instead of
 * Callers should check the `failures` array and surface
 * warnings to users when entities could not be discovered.
 *
 * @param params - Configuration for the traversal
 * @param params.entityData - The root entity to start from (optional, can start from relationships only)
 * @param params.entitySchema - Schema for interpreting the root entity's structure
 * @param params.loadEntityRelationshipsSchemas - Function to load schemas for referenced entity types
 * @param params.findEntityOnSourceByDomainEntityId - Function to resolve domain entity references to config IDs
 * @param params.fetchEntityOnSourceById - Function to fetch full entity data by config ID
 *
 * @returns Promise resolving to an object containing:
 *   - `entities`: Map where keys are entity IDs and values contain the entity data
 *     along with its direct dependencies (for topological sorting)
 *   - `failures`: Array of discovery failures that occurred (entities that couldn't be fetched)
 *
 * @throws {Error} If any required schemas cannot be loaded
 *
 * @example
 * ```typescript
 * const { entities, failures } = await getAllReferencedEntities({
 *   entityData: myRootEntity,
 *   entitySchema: rootSchema,
 *   loadEntityRelationshipsSchemas: async (types) => [...],
 *   findEntityOnSourceByDomainEntityId: async (domainEntityId, domainEntityType) => {...},
 *   fetchEntityOnSourceById: async (configId) => {...}
 * });
 *
 * if (failures.length > 0) {
 *   console.warn('Some referenced entities could not be discovered:', failures);
 * }
 * ```
 */
export async function getAllReferencedEntities({
  entityData,
  entitySchema,
  loadEntityRelationshipsSchemas,
  findEntityOnSourceByDomainEntityId,
  fetchEntityOnSourceById,
}: GetAllReferencedEntitiesParams): Promise<GetAllReferencedEntitiesResult> {
  // The domain entity schema (entitySchema) has domain attributes directly
  // under `properties.attributes` (e.g., cardData, contentData), but the
  // config entity data from the API nests them under
  // `attributes.domainEntityAttributes`. Merge the schema so paths match
  // the actual data structure for root-level reference extraction.
  // NOTE: callers must pass domainEntity.schema (raw domain format). Passing
  // a pre-merged configEntity.schema would double-nest domainEntityAttributes.
  const rootSchema =
    getConfigEntitySchemaWithDomainEntityAttributesAndRelationships(
      entitySchema,
      entitySchema,
    );

  // Step 1: Extract linked references (domainEntityType/domainEntityId pairs) from the root entity
  const rootEntityLinkedRefs: LinkedEntityReference[] = getEntityReferenceIds(
    entityData,
    rootSchema,
  ).filter(isLinkedEntityRef);

  // Step 2: Load initial schemas for linked entity types discovered on the root entity
  const linkedEntityTypes = Array.from(
    new Set(rootEntityLinkedRefs.map((ref) => ref.domainEntityType)),
  );

  // Load schemas for all entity types we discovered in the root entity's data.
  // This function may merge these with types already being tracked by the caller.
  const schemas = await loadEntityRelationshipsSchemas(linkedEntityTypes);

  // Track all types we've loaded to avoid re-fetching
  const loadedTypes = new Set(schemas.map((s) => s.type));

  const failedSchemas = schemas
    .filter(({ status, schema }) => status === 'error' || !schema)
    .map(({ type }) => type);

  if (failedSchemas.length > 0) {
    throw new Error(
      `Unable to load schema for entity types: ${failedSchemas.join(', ')}`,
    );
  }

  // Step 3: Traverse all references until closure is reached
  // Cache of schemas we have already loaded so recursion can reuse them
  const schemaByType = new Map<string, EntityDetailSchema>();
  schemas.forEach(({ type, schema: loadedSchema, status }) => {
    if (loadedSchema && status === 'success') {
      schemaByType.set(type, loadedSchema);
    }
  });

  // Track in-flight schema loads to avoid duplicating work for the same type.
  // Used by resolveSchema() to coordinate concurrent requests for the same schema.
  const schemaPromises = new Map<string, Promise<EntityDetailSchema>>();

  // Create context object for schema resolution
  const resolveSchemaContext: ResolveSchemaContext = {
    schemaByType,
    schemaPromises,
    loadEntityRelationshipsSchemas,
    schemas,
    loadedTypes,
  };

  // Collect every fetched config entity with its dependencies to return to caller
  // Map<entityId, { entity, dependencies }>
  const entitiesWithDependencies = new Map<string, EntityWithDependencies>();

  // Track discovery failures to surface to the caller
  const failures: DiscoveryFailure[] = [];

  // Track visited entities to prevent infinite loops and duplicate fetches.
  // We need both because:
  // - resolvedDomainKeys: caches domain entity → config entity ID resolution
  //   (e.g., "images:logo-123" → "cfg-456") so multiple parents referencing
  //   the same domain entity each get it as a dependency without re-resolving.
  // - visitedConfigEntityIds: prevents re-fetching the same config entity (e.g., "cfg-456")
  // Multiple domain entities can map to the same config entity, and vice versa.
  const resolvedDomainKeys = new Map<string, string>();
  const visitedConfigEntityIds = new Set<string>();

  // Track failed lookups to avoid re-attempting and duplicate failure entries
  const failedDomainKeys = new Set<string>();
  const failedConfigEntityIds = new Set<string>();

  /**
   * Resolve a domain reference (type + domainEntityId) to a configEntityId.
   * Returns undefined if the entity cannot be found, and tracks the failure.
   *
   * @param reference - The domain entity reference to resolve
   * @param referencedFrom - The ID of the parent entity containing this reference (for error context)
   */
  async function resolveDomainReference(
    reference: LinkedEntityReference,
    referencedFrom?: string,
  ): Promise<string | undefined> {
    const { domainEntityId, domainEntityType } = reference;
    const key = `${domainEntityType}:${domainEntityId}`;

    if (!domainEntityId || failedDomainKeys.has(key)) {
      return undefined;
    }

    // Return cached config entity ID if already resolved, so multiple
    // parents referencing the same entity each record it as a dependency.
    if (resolvedDomainKeys.has(key)) {
      return resolvedDomainKeys.get(key);
    }

    const sourceResult = await withRetry({
      operation: async () => {
        const result = await findEntityOnSourceByDomainEntityId(
          domainEntityId,
          domainEntityType,
        );
        return {
          configEntityId: result.data?.id,
          found: result.status === 'success',
        };
      },
      onError: (error) => {
        failedDomainKeys.add(key);
        failures.push({
          type: 'domain_resolution_failed',
          domainEntityId,
          domainEntityType,
          referencedFrom,
          message: `Failed to resolve ${domainEntityType} "${domainEntityId}": ${error.message}`,
        });
      },
    });

    if (!sourceResult) {
      return undefined;
    }

    if (!sourceResult.found || !sourceResult.configEntityId) {
      // Entity wasn't found on source environment - this might be expected
      // (e.g., optional reference), so we track it but at a lower severity
      failedDomainKeys.add(key);
      failures.push({
        type: 'domain_resolution_failed',
        domainEntityId,
        domainEntityType,
        referencedFrom,
        message: `Could not find ${domainEntityType} "${domainEntityId}"`,
      });
      return undefined;
    }

    resolvedDomainKeys.set(key, sourceResult.configEntityId);
    return sourceResult.configEntityId;
  }

  /**
   * Fetch an entity by configEntityId, store it with its dependencies, then recursively
   * visit its references.
   *
   * Two types of references are followed:
   * (1) Schema-derived domain entity references: Fields marked with x-entity-reference
   *     annotations (e.g., "logoId" referencing an "images" domain entity)
   * (2) JSON:API relationships: Direct config entity IDs found in the `relationships`
   *     object (e.g., related entities already persisted as config entities)
   *
   * This function calls itself recursively to traverse the entire reference graph.
   * Failures are tracked in the `failures` array for the caller to surface.
   *
   * @param configEntityId - The config entity ID to fetch
   * @param referencedFrom - The ID of the parent entity that referenced this one (for error context)
   */
  async function fetchEntityAndFollowReferences(
    configEntityId: string,
    referencedFrom?: string,
    knownDomainEntityType?: string,
  ): Promise<void> {
    // BASE CASE: Stop recursion if already visited (prevents infinite loops)
    if (!configEntityId || visitedConfigEntityIds.has(configEntityId)) {
      return;
    }

    visitedConfigEntityIds.add(configEntityId);

    const fetchResult = await withRetry({
      operation: () =>
        fetchEntityOnSourceById(configEntityId, knownDomainEntityType),
      onError: (error) => {
        failedConfigEntityIds.add(configEntityId);
        failures.push({
          type: 'fetch_failed',
          configEntityId,
          domainEntityType: knownDomainEntityType,
          referencedFrom,
          message: `Failed to fetch config entity "${configEntityId}": ${error.message}`,
        });
      },
    });

    if (!fetchResult) {
      return;
    }

    // fetchEntityOnSourceById returns an untyped `data` field; the response
    // is always a ConfigEntityDetail when the fetch succeeds.
    const fetchedEntity = fetchResult.data as ConfigEntityDetail | undefined;
    if (!fetchedEntity) {
      // Entity fetch succeeded but returned no data - track this failure
      failedConfigEntityIds.add(configEntityId);
      failures.push({
        type: 'no_data_returned',
        configEntityId,
        referencedFrom,
        message: `Config entity "${configEntityId}" fetch returned no data`,
      });
      return;
    }

    const domainEntityId =
      fetchedEntity.attributes?.entityMetadata?.domainEntityId;
    const domainEntityType =
      fetchedEntity.attributes?.entityMetadata?.domainEntityType;

    if (domainEntityId && domainEntityType) {
      resolvedDomainKeys.set(
        `${domainEntityType}:${domainEntityId}`,
        fetchedEntity.id,
      );
    }

    // Collect all direct dependency config entity IDs for this entity
    const directDependencyIds: string[] = [];

    // Resolve the full config schema for this entity type (if known)
    // Used for both domain reference extraction and relationship type lookups
    let configSchema: EntityDetailSchema | undefined;

    // Part 1: Follow schema-derived domain entity references
    // Extract any domain entity IDs from fields that have x-entity-reference annotations
    if (domainEntityType) {
      const entitySchemaForType = await resolveSchema(
        domainEntityType,
        resolveSchemaContext,
      );
      // Merge the domain entity schema with config entity wrapper structure
      // (entitySchema = generic config wrapper, entitySchemaForType = domain-specific schema)
      configSchema =
        getConfigEntitySchemaWithDomainEntityAttributesAndRelationships(
          entitySchema,
          entitySchemaForType,
        );
      const linkedRefs = getEntityReferenceIds(
        fetchedEntity,
        configSchema,
      ).filter(isLinkedEntityRef);

      // Resolve domain references to config entity IDs and recursively follow them
      // Pass fetchedEntity.id as referencedFrom for error context
      await Promise.all(
        linkedRefs.map(async (ref) => {
          const configId = await resolveDomainReference(ref, fetchedEntity.id);
          if (configId) {
            directDependencyIds.push(configId);
            await fetchEntityAndFollowReferences(
              configId,
              fetchedEntity.id,
              ref.domainEntityType,
            );
          }
        }),
      );
    }

    // Part 2: Follow JSON:API relationship config entity IDs
    // These are direct references to other config entities already in the system
    // Pass the resolved config schema so we can extract domain entity types from schema definitions
    const relatedConfigRefs = extractRelationshipConfigEntityRefs(
      fetchedEntity,
      configSchema,
    );

    // Add relationship IDs to dependencies
    directDependencyIds.push(...relatedConfigRefs.map((ref) => ref.id));

    // RECURSIVE CALL: Follow each relationship config entity ID
    // Pass fetchedEntity.id as referencedFrom for error context
    await Promise.all(
      relatedConfigRefs.map((ref) =>
        fetchEntityAndFollowReferences(
          ref.id,
          fetchedEntity.id,
          ref.domainEntityType,
        ),
      ),
    );

    // Build dependencies array with domainEntityType for each dependency
    const dependencies: EntityDependencyRef[] = [
      ...new Set(directDependencyIds),
    ].map((depId) => {
      // Try to get the domainEntityType from the already-fetched entity
      const depEntity = entitiesWithDependencies.get(depId);
      const depDomainEntityType =
        depEntity?.entity.attributes?.entityMetadata?.domainEntityType ?? '';
      return {
        id: depId,
        domainEntityType: depDomainEntityType,
      };
    });

    // Store the entity with its dependencies
    entitiesWithDependencies.set(fetchedEntity.id, {
      entity: fetchedEntity,
      dependencies,
    });
  }

  // RECURSION ENTRY POINT: Seed the traversal with references found directly on the root entity.
  // Start with any JSON:API relationship config IDs (with domain entity type info).
  const initialRelationshipConfigRefs = extractRelationshipConfigEntityRefs(
    entityData,
    rootSchema,
  );

  // Deduplicate domain entity references (type:id pairs) discovered on the root entity.
  // Multiple fields might reference the same domain entity.
  const uniqueInitialReferences = Array.from(
    new Map(
      rootEntityLinkedRefs.map((ref) => [
        `${ref.domainEntityType}:${ref.domainEntityId}`,
        ref,
      ]),
    ).values(),
  );

  // Get the root entity ID for error context (if available)
  const rootEntityId = entityData?.id;

  // Collect config entity IDs that are direct dependencies of the root entity.
  // These are used by the caller to correctly represent the root's children
  // in the tree (as opposed to making all discovered entities root children).
  const rootDirectDependencyIds: string[] = [];

  // Kick off recursive traversal from the root entity's references.
  // The recursion will fan out as each fetched entity discovers its own references.
  await Promise.all([
    ...uniqueInitialReferences.map(async (ref) => {
      const configId = await resolveDomainReference(ref, rootEntityId);
      if (configId) {
        rootDirectDependencyIds.push(configId);
        await fetchEntityAndFollowReferences(
          configId,
          rootEntityId,
          ref.domainEntityType,
        );
      }
    }),
    ...initialRelationshipConfigRefs.map(async (ref) => {
      rootDirectDependencyIds.push(ref.id);
      await fetchEntityAndFollowReferences(
        ref.id,
        rootEntityId,
        ref.domainEntityType,
      );
    }),
  ]);

  // Include the root entity in the dependency graph with its actual direct
  // dependencies so callers receive a complete tree without special handling.
  if (entityData?.id && !entitiesWithDependencies.has(entityData.id)) {
    const rootDirectDependencies: EntityDependencyRef[] = [
      ...new Set(rootDirectDependencyIds),
    ].map((depId) => {
      const depEntity = entitiesWithDependencies.get(depId);
      const depDomainEntityType =
        depEntity?.entity.attributes?.entityMetadata?.domainEntityType ?? '';
      return { id: depId, domainEntityType: depDomainEntityType };
    });

    entitiesWithDependencies.set(entityData.id, {
      entity: entityData as ConfigEntityDetail,
      dependencies: rootDirectDependencies,
    });
  }

  return {
    entities: entitiesWithDependencies,
    failures,
    schemas: schemaByType,
  };
}
